/* eslint-disable @typescript-eslint/no-explicit-any */
import { QuestionOutput, QuestionType } from '@/api/gql/graphql';
import {
  useCreateQuizz,
  useGetQuizz,
  useRemoveQuizz,
  useUpdateQuizz,
} from '@/api/hooks/useQuizz';
import { createMenuItem } from '@/components/Common/DropdownMenu/MenuDropdown.utils';
import { QuizzFormValues, useHandleQuizzForm } from '@/hooks/useQuizzForm';
import { useToast } from '@/hooks/useToast';
import { useQuizStore } from '@/store/QuizStore/QuizStore';
import { useParams } from 'react-router-dom';
import { useQuestionLogic } from '@/hooks/useQuestionLogic';
import axios from 'axios';
import { createFile } from '@/api/fileUpload';

export type QuizFormMode = 'create' | 'update';

export const useQuizz = () => {
  const {
    openQuizModal,
    setInitialSelectedOptions,
    initialSelectedOptions,
    isOpenQuizDeleteModal,
    openQuizDeleteModal,
    selectedQuizz,
    setSelectedQuizz,
    formMode,
    setFormMode,
  } = useQuizStore();
  const { lessonId, moduleId } = useParams();
  const payload = {
    ...(moduleId ? { section: moduleId } : { lesson: lessonId }),
  };
  const { showToast } = useToast();

  const { register, handleSubmit, setValue, errors, reset, getValues } =
    useHandleQuizzForm('create');

  const {
    register: registerUpdate,
    handleSubmit: handleSubmitUpdate,
    errors: updateErrors,
    setValue: setUpdateValue,
    reset: resetUpdate,
  } = useHandleQuizzForm('update');

  const {
    selectedType,
    setSelectedType,
    response1Check,
    setResponse1Check,
    response2Check,
    setResponse2Check,
    response3Check,
    setResponse3Check,
    response4Check,
    setResponse4Check,
    formatResponses,
    resetResponses,
  } = useQuestionLogic();

  const { data: quizzList, isLoading: loadingQuizz } = useGetQuizz({
    ...payload,
  });

  const { mutate: creationQuizz, isPending: loadingQuizzCreation } =
    useCreateQuizz();

  const { mutate: updateQuizz, isPending: loadingQuizzUpdate } =
    useUpdateQuizz();

  const { mutate: onDeleteQuizz, isPending: isDeletePending } =
    useRemoveQuizz();

  const QuizzQuestionType = [
    { _id: QuestionType.Qcm, name: QuestionType.Qcm },
    { _id: QuestionType.TrueOrFalse, name: QuestionType.TrueOrFalse },
    { _id: QuestionType.MissingWords, name: QuestionType.MissingWords },
  ];

  const handleSelectedQuizz = (quizz: QuestionOutput) => {
    setSelectedQuizz(quizz);
  };

  const createSingleQuestion = async (values: QuizzFormValues) => {
    try {
      const responses = formatResponses(values);
      const hasAtLeastOneTrue = responses.some(
        (response: { isRight: boolean }) => response.isRight,
      );

      if (selectedType) {
        const type = Object.values(selectedType)[0];
        if (!hasAtLeastOneTrue) {
          const warningMessage =
            type === QuestionType.Qcm || type === QuestionType.MissingWords
              ? `Pour un ${type}, il doit y avoir au moins une réponse correcte.`
              : 'Pour TRUE_OR_FALSE, il doit y avoir au moins une réponse correcte.';
          showToast(warningMessage, 'warning', {
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
      }

      let imageUrl = null;
      if (values.picture) {
        const token = localStorage.getItem('authToken') ?? '';
        const response = await axios(createFile(values.picture, token));
        if (response.status !== 201) {
          throw new Error("Erreur lors de l'ajout de l'image");
        }
        imageUrl = response.data?.publicUrl || response.data;
      }

      creationQuizz(
        {
          name: values.name ?? '',
          type: values?.type as QuestionType,
          responses,
          ...payload,
          description: values.description,
          image: imageUrl,
        },
        {
          onSuccess: () => {
            showToast('Quizz ajouté', 'success', {
              autoClose: 3000,
              position: 'top-right',
            });

            resetResponses();
            setInitialSelectedOptions &&
              setInitialSelectedOptions({
                _id: QuestionType.Qcm,
                name: QuestionType.Qcm,
              });

            const currentTypeValue = getValues('type');
            reset({
              name: '',
              description: '',
              picture: undefined,
              reponse1: '',
              reponse2: '',
              reponse3: '',
              reponse4: '',
              type: currentTypeValue,
            });
            setValue('picture', undefined);
          },
          onError: (error: { message: string }) => {
            showToast(error?.message, 'error', {
              autoClose: 3000,
              position: 'top-right',
            });
          },
        },
      );
    } catch (error) {
      console.error('Error:', error);
      showToast(
        "Une erreur s'est produite lors de la création du quiz",
        'error',
        {
          autoClose: 3000,
          position: 'top-right',
        },
      );
    }
  };

  const updateSingleQuestion = (values: QuizzFormValues) => {
    const responses = formatResponses(values);
    updateQuizz(
      {
        input: {
          name: values.name ?? '',
          type: selectedQuizz?.type as QuestionType,
          description: values.description ?? '',
          responses,
        },
        updateQuestionId: selectedQuizz?._id,
      },
      {
        onSuccess: () => {
          showToast('Quiz mis à jour', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          setInitialSelectedOptions && setInitialSelectedOptions(null);
          resetUpdate();
          setFormMode('create');
          setResponse1Check(false);
          setResponse2Check(false);
          setResponse3Check(false);
          setResponse4Check(false);
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const openDeleteQuizz = () => {
    onDeleteQuizz(
      {
        removeQuestionId: selectedQuizz?._id,
      },
      {
        onSuccess: () => {
          showToast('Question supprimée', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          openQuizDeleteModal();
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const handleFormMode = () => {
    setFormMode('update');
  };

  const quizzMenuItems = [
    createMenuItem('Modifier', '', handleFormMode),
    createMenuItem('Supprimer', 'text-[#EA242C]', openQuizDeleteModal),
  ];

  return {
    quizzList,
    lessonId,
    loadingQuizz,
    openQuizModal,
    handleSelectedQuizz,
    setSelectedQuizz,
    selectedQuizz,
    QuizzQuestionType,
    quizzMenuItems,
    register,
    registerUpdate,
    handleSubmit,
    handleSubmitUpdate,
    setValue,
    setUpdateValue,
    errors,
    updateErrors,
    reset,
    resetUpdate,
    updateSingleQuestion,
    createSingleQuestion,
    handleSelectedType: setSelectedType,
    selectedType,
    response1Check,
    setResponse1Check,
    response2Check,
    setResponse2Check,
    response3Check,
    setResponse3Check,
    response4Check,
    setResponse4Check,
    loadingQuizzCreation,
    initialSelectedOptions,
    isOpenQuizDeleteModal,
    openQuizDeleteModal,
    isDeletePending,
    openDeleteQuizz,
    formMode,
    setFormMode,
    setInitialSelectedOptions,
    loadingQuizzUpdate,
  };
};
